import { Input, OnInit } from '@angular/core';
import { Component } from '@angular/core';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import {
    channelStatePropertyMap, INotification, INotificationRecipient, NotificationChannel, notificationChannels,
    NotificationChannelState, NotificationRequestStatus, notificationRequestStatuses, NotificationStatus
} from '../../../models/Notification';
import { AppService } from '../../../services/app.service';
import { ITableColumn } from '../../../shared/table/table.component';

interface IRecipientRow {
    recipient: INotificationRecipient;
    channels: IChannelRequest[];
    searchName: string;
    searchLanguage: string;
}

interface IChannelRequest {
    name: NotificationChannel;
    isAvailable: boolean;
    isRejected?: boolean;
    status?: NotificationRequestStatus;
    sent?: Date;
    error?: string;
}

@Component({
    selector: 'app-notification-view',
    templateUrl: './notification-view.component.html',
    styleUrls: ['./notification-view.component.scss']
})
export class NotificationViewComponent implements OnInit {
    constructor(private app: AppService) {}

    @Input() data: INotification;

    readonly recipientColumns: ITableColumn[] = [
        { property: 'fullName', label: 'notification_lblRecipient', sorts: true },
        { property: 'language', label: 'notification_lblLanguage', sorts: true },
        ...notificationChannels.map(t => ({ label: `notification_channel${t}` }))
    ];

    readonly channels: { name: NotificationChannel, required: boolean }[] = [];
    readonly allChannels = notificationChannels;

    readonly stats: {
        channel: NotificationChannel,
        approved: number,
        rejected: number,
        failed: number
    }[] = [];

    readonly options: {
        requestStatus: { value: string, text: string }[]
    } = {
            requestStatus: notificationRequestStatuses.map(t => {
                return {
                    text: this.app.translate(`notification_requestStatus${t}`),
                    value: t.toString()
                };
            }).concat([
                {
                    text: this.app.translate(`notification_requestRejected`),
                    value: 'rejected'
                }
            ])
        };

    readonly recipientFilterValues: {
        search: string,
        channels: { [name: string]: string[] }
    } = {
            search: '',
            channels: notificationChannels.reduce((all, t) => {
                all[t] = [];
                return all;
            }, {})
        };

    isFinal: boolean;
    recipientRows: IRecipientRow[] = [];

    private allRecipientRows: IRecipientRow[] = [];
    private readonly recipientSearchSubj = new Subject<string>();

    ngOnInit() {
        if (!this.data) return;

        this.isFinal = this.data.Status == NotificationStatus.Pending || this.data.Status == NotificationStatus.Finished;

        const recRows: IRecipientRow[] = [];

        this.recipientSearchSubj.pipe(debounceTime(300), distinctUntilChanged()).subscribe(term => {
            this.filterRecipients();
        });

        notificationChannels.forEach(t => {
            const state = this.data[channelStatePropertyMap[t]];

            if (state >= NotificationChannelState.Available) {
                this.channels.push({
                    name: t,
                    required: state == NotificationChannelState.Required
                });
            }

            this.stats.push({
                channel: t,
                approved: 0,
                rejected: 0,
                failed: 0
            });
        });

        this.data.Recipients.forEach(rec => {
            const requests = this.data.Requests.filter(t => t.RecipientId == rec.Id);
            const channels: IChannelRequest[] = [];

            notificationChannels.forEach(ch => {
                const req = requests.find(rq => rq.Channel == ch);
                const stat = this.stats.find(s => s.channel == ch);
                const channelReq: IChannelRequest = {
                    name: ch,
                    isAvailable: this.channels.some(t => t.name == ch)
                };

                if (req) {
                    channelReq.error = req.Error;
                    channelReq.sent = req.Sent;
                    channelReq.status = req.Status;

                    stat.approved++;
                    if (req.Error) stat.failed++;
                } else {
                    channelReq.isRejected = true;
                    stat.rejected++;
                }

                channels.push(channelReq);
            });

            recRows.push({
                recipient: rec,
                channels,
                searchLanguage: rec.Language.toLowerCase(),
                searchName: `${rec.Name} ${rec.Surname} ${rec.Upn}`.toLowerCase()
            });
        });

        this.recipientRows = recRows;
        this.allRecipientRows = recRows;
    }

    filterRecipients() {
        const search = (this.recipientFilterValues.search || '').toLowerCase();
        const hasChannelFilter = Object.keys(this.recipientFilterValues.channels).filter(t => this.recipientFilterValues.channels[t].length > 0).length > 0;

        let rows: IRecipientRow[] = [];

        if (!search && !hasChannelFilter) {
            rows = this.allRecipientRows;
        } else {
            rows = this.allRecipientRows.filter(t => {
                if (hasChannelFilter) {
                    let channelStatusFound = false;

                    for (let i = 0; i < t.channels.length; i++) {
                        const channelName = t.channels[i].name;
                        const channelStatus = t.channels[i].status;
                        const selectedChannelStatus = this.recipientFilterValues.channels[channelName];

                        if (selectedChannelStatus.includes(channelStatus)
                            || (selectedChannelStatus.includes('rejected') && t.channels[i].isRejected)
                        ) {
                            channelStatusFound = true;
                            break;
                        }
                    }

                    if (!channelStatusFound) return false;
                }

                if (search && !t.searchName.includes(search)) return false;

                return true;
            });
        }

        this.recipientRows = [...rows];
    }

    searchRecipients(term: string) {
        this.recipientSearchSubj.next(term);
    }

    isChannelAvailable(channel: NotificationChannel): boolean {
        return this.channels.some(t => t.name == channel);
    }
}
